import * as Api from '@ViewModels';
import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components-v2/Select';
import { useDateRangeFilter } from '../../../../components/AdvancedFilters/hooks/useDateRangeFilter';
import { CustomDateRange } from '../../../../components/CustomDateRange';
import { bs } from '../../../../styles/styles';
import { compoundFiltersStyles } from './styles';

interface DateOption {
	id: PresetId;
	title: string;
	filter?: Api.IContactFilterCriteria;
}

enum PresetId {
	None = 'None',
	Never = 'Never',
	Custom = 'Custom',
	OneYear = 'OneYear',
	SixMonths = 'SixMonths',
	ThreeMonths = 'ThreeMonths',
	TwoMonths = 'TwoMonths',
	OneMonth = 'OneMonth',
}
const presets = [
	{
		id: PresetId.None,
		title: 'Select an option...',
	},
	{
		id: PresetId.Never,
		title: 'Never',
		filter: {
			op: Api.FilterOperator.Lte,
			property: Api.ContactFilterCriteriaProperty.LastCommunicationDate,
			value: 'never',
		},
	},
	{
		id: PresetId.OneYear,
		title: '1 year ago',
		filter: {
			op: Api.FilterOperator.Lte,
			property: Api.ContactFilterCriteriaProperty.LastCommunicationDate,
			values: { years: '-1' },
		},
	},
	{
		id: PresetId.SixMonths,
		title: '6 months ago',
		filter: {
			op: Api.FilterOperator.Lte,
			property: Api.ContactFilterCriteriaProperty.LastCommunicationDate,
			values: { months: '-6' },
		},
	},
	{
		id: PresetId.ThreeMonths,
		title: '3 months ago',
		filter: {
			op: Api.FilterOperator.Lte,
			property: Api.ContactFilterCriteriaProperty.LastCommunicationDate,
			values: { months: '-3' },
		},
	},
	{
		id: PresetId.TwoMonths,
		title: '2 months ago',
		filter: {
			op: Api.FilterOperator.Lte,
			property: Api.ContactFilterCriteriaProperty.LastCommunicationDate,
			values: { months: '-2' },
		},
	},
	{
		id: PresetId.OneMonth,
		title: '1 month ago',
		filter: {
			op: Api.FilterOperator.Lte,
			property: Api.ContactFilterCriteriaProperty.LastCommunicationDate,
			values: { months: '-1' },
		},
	},
	{
		id: PresetId.Custom,
		title: 'Custom',
	},
] as DateOption[];

const getInitialPresetId = (filters: ReturnType<typeof useDateRangeFilter>) => {
	if (filters.startDate || filters.endDate) {
		return PresetId.Custom;
	}

	if (!filters.relativeDate) {
		return PresetId.None;
	}

	if (filters.relativeDate.value === 'never') {
		return PresetId.Never;
	}

	if (filters.relativeDate.values?.['years'] === '-1') {
		return PresetId.OneYear;
	}

	if (filters.relativeDate.values?.['months'] === '-6') {
		return PresetId.SixMonths;
	}

	if (filters.relativeDate.values?.['months'] === '-3') {
		return PresetId.ThreeMonths;
	}

	if (filters.relativeDate.values?.['months'] === '-2') {
		return PresetId.TwoMonths;
	}

	if (filters.relativeDate.values?.['months'] === '-1') {
		return PresetId.OneMonth;
	}

	return PresetId.Custom;
};

export function LastCommunicationDateDropdown({
	dateRangeFilters,
	saveChanges,
}: {
	dateRangeFilters: ReturnType<typeof useDateRangeFilter>;
	saveChanges: () => void;
}) {
	const [selectedId, setSelectedId] = React.useState<PresetId>(getInitialPresetId(dateRangeFilters));
	const [showCustom, setShowCustom] = React.useState(false);

	const onSelectionChanged = (id: PresetId) => {
		setSelectedId(id);
		if (id === PresetId.Custom) {
			return;
		}

		const preset = presets.find(x => x.id === id)?.filter;
		dateRangeFilters.setRelativeDate(preset);
		if (preset) {
			dateRangeFilters.setStartDate(null);
			dateRangeFilters.setEndDate(null);
		}
		saveChanges();
	};

	React.useEffect(() => {
		setShowCustom(selectedId === PresetId.Custom);
	}, [selectedId]);

	React.useEffect(() => {
		setSelectedId(getInitialPresetId(dateRangeFilters));
	}, [dateRangeFilters]);

	return (
		<>
			<Select
				items={presets.map(p => ({ label: p.title, value: p.id }))}
				value={selectedId}
				onValueChange={onSelectionChanged}
			>
				<SelectTrigger styles={[bs.wFull]}>
					<SelectValue placeholder='Select an option...' />
				</SelectTrigger>
				<SelectContent>
					{presets.map(o => (
						<SelectItem key={o.id} value={o.id}>
							{o.title}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			{showCustom && (
				<CustomDateRange
					styles={[compoundFiltersStyles.lastCommunicationDateDropdown]}
					startDateRequired={false}
					forceSmallText
					from={dateRangeFilters.startDate || null}
					to={dateRangeFilters.endDate || null}
					onChange={(from, to) => {
						dateRangeFilters.setRelativeDate(null);
						dateRangeFilters.setStartDate(from);
						if (to !== from) {
							dateRangeFilters.setEndDate(to);
						}

						saveChanges();
					}}
					resetDates={() => {
						dateRangeFilters.refreshDateFilter([]);
						saveChanges();
					}}
					showTextInput
				/>
			)}
		</>
	);
}
